<template>
  <div class="mock-screen" :class="`mock-${type}`" aria-hidden="true">
    <div class="mock-topbar">
      <i class="mock-brand">订舱助手</i>
      <i></i>
      <i></i>
      <i></i>
    </div>
    <div class="mock-body">
      <aside class="mock-rail">
        <i>{{ title }}</i>
        <i></i>
        <i></i>
        <i></i>
      </aside>
      <div class="mock-main">
        <div class="mock-crumb">
          <i>{{ title }}</i>
          <i></i>
        </div>

        <template v-if="type === 'list'">
          <div class="mock-metrics">
            <i v-for="metric in metrics" :key="metric">{{ metric }}</i>
          </div>
          <div class="mock-filters">
            <i v-for="n in 4" :key="n"></i>
            <b>查询</b>
          </div>
          <div class="mock-toolbar">
            <i>订单进度</i>
            <b></b>
          </div>
          <div class="mock-table">
            <div class="mock-table-head">
              <i v-for="n in 6" :key="n"></i>
            </div>
            <div v-for="(status, index) in statusList" :key="index" class="mock-table-row">
              <i v-for="n in 4" :key="n"></i>
              <b>{{ status }}</b>
              <i></i>
            </div>
          </div>
        </template>

        <div v-else-if="type === 'form'" class="mock-form-layout">
          <div class="mock-form-main">
            <div v-for="section in formSections" :key="section.title" class="mock-form-section">
              <span>{{ section.title }}</span>
              <div class="mock-form-fields">
                <i v-for="n in section.fields" :key="n"></i>
              </div>
            </div>
          </div>
          <div class="mock-form-side">
            <span>本次订舱</span>
            <i v-for="n in 4" :key="n"></i>
            <b>提交订舱</b>
          </div>
        </div>

        <div v-else class="mock-record-layout">
          <div class="mock-record-summary">
            <span>订舱概览</span>
            <i></i>
            <i></i>
            <i></i>
            <div></div>
            <i></i>
            <i></i>
          </div>
          <div class="mock-record-history">
            <span>操作记录</span>
            <div v-for="event in recordEvents" :key="event" class="mock-record-event">
              <i>{{ event }}</i>
              <b></b>
              <em></em>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 模板用的常量，放在模块级 + computed 暴露：不进 data()，避免被 Vue 递归做成响应式
const METRICS = ['待关注', '处理中', '放舱成功', '全部']
const STATUS_LIST = ['待处理', '已完成', '进行中', '待处理']
const FORM_SECTIONS = [
  { title: '基础信息', fields: 4 },
  { title: '航线与船期', fields: 6 },
  { title: '货物信息', fields: 2 }
]
const RECORD_EVENTS = ['创建订舱', '提交订舱', '进度更新', '结果回传', '后续处理']
const TYPES = ['list', 'form', 'records']

/**
 * 产品介绍页的界面示意：用抽象线框表达订舱助手的三张核心界面，
 * 不展示完整字段与真实订单数据，避免宣传页与版本迭代脱节。
 * 整体 aria-hidden，屏幕阅读器读外层 .preview-art 的 role=img + aria-label。
 */
export default {
  name: 'BookingPreviewMock',
  props: {
    type: {
      type: String,
      default: 'list',
      validator: value => TYPES.includes(value)
    },
    // 标题由调用方传入（与预览 tab 数据同源），避免两处维护
    title: {
      type: String,
      default: ''
    }
  },
  computed: {
    metrics() {
      return METRICS
    },
    statusList() {
      return STATUS_LIST
    },
    formSections() {
      return FORM_SECTIONS
    },
    recordEvents() {
      return RECORD_EVENTS
    }
  }
}
</script>

<style lang="scss" scoped>
.mock-screen {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  color: #526781;
  font-size: 12px;
  font-weight: 600;
  background: #fff;
  box-shadow: inset 0 0 0 1px #e2eaf4;

  i,
  b,
  em,
  span {
    display: block;
    flex: none;
    font-style: normal;
  }
}

.mock-topbar {
  display: flex;
  align-items: center;
  gap: 1.5%;
  height: 6.5%;
  min-height: 18px;
  padding: 0 2%;
  border-bottom: 1px solid #e3eaf3;
  background: #fff;

  i {
    width: 4%;
    height: 17%;
    min-height: 3px;
    border-radius: 2px;
    background: #cfdaea;
  }

  .mock-brand {
    width: auto;
    height: auto;
    min-height: 0;
    margin-right: auto;
    background: none;
    color: #1974bc;
    font-size: 14px;
    font-weight: 800;
    white-space: nowrap;
  }
}

.mock-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.mock-rail {
  display: flex;
  flex-direction: column;
  gap: 7%;
  width: 12%;
  min-width: 34px;
  padding: 3% 1.5%;
  background: #f5f8fd;
  border-right: 1px solid #e4ebf5;

  i {
    width: 82%;
    height: 1.4%;
    min-height: 4px;
    border-radius: 2px;
    background: #cedaea;

    &:nth-child(2) {
      background: #a8cdfb;
    }

    &:nth-child(3) {
      width: 63%;
    }

    &:first-child {
      width: auto;
      height: auto;
      min-height: 0;
      background: none;
      color: #1767db;
      font-weight: 800;
      white-space: nowrap;
    }
  }
}

.mock-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2.3%;
  min-width: 0;
  padding: 2% 2.5%;
  background: #fbfdff;
}

.mock-crumb {
  display: flex;
  gap: 1%;
  height: 4%;
  min-height: 13px;

  i {
    width: 8%;
    height: 55%;
    border-radius: 2px;
    background: #becbda;

    &:first-child {
      width: auto;
      height: auto;
      background: none;
      color: #294b73;
      font-size: 13px;
      font-weight: 800;
      white-space: nowrap;
    }

    & + i {
      width: 5%;
      background: #d9e3ef;
      display: none;
    }
  }
}

.mock-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.3%;
  height: 17%;

  i {
    position: relative;
    height: 100%;
    padding: 5% 9% 0;
    border: 1px solid #dae5f4;
    border-radius: 3px;
    background: #fff;
    white-space: nowrap;

    &:before,
    &:after {
      content: '';
      position: absolute;
      left: 9%;
      height: 7%;
      border-radius: 2px;
      background: #d3deeb;
    }

    &:before {
      top: 21%;
      width: 38%;
      display: none;
    }

    &:after {
      top: 52%;
      width: 19%;
      height: 17%;
      background: #9bc3f3;
    }

    &:nth-child(2):after {
      background: #f6c68c;
    }

    &:nth-child(3):after {
      background: #85c7bd;
    }
  }
}

.mock-filters {
  display: grid;
  grid-template-columns: repeat(4, 1fr) 10%;
  align-items: center;
  gap: 1.3%;
  height: 14%;
  padding: 1.5%;
  border: 1px solid #e2eaf4;
  background: #fff;

  i {
    height: 55%;
    border: 1px solid #d6e2ef;
    border-radius: 2px;
    background: #f9fbff;
  }

  b {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 55%;
    border-radius: 2px;
    background: #2173d9;
    color: #fff;
    font-weight: 700;
    white-space: nowrap;
  }
}

.mock-toolbar {
  display: flex;
  align-items: center;
  gap: 1%;
  height: 7%;
  border-bottom: 1px solid #e1e9f3;

  i {
    width: 8%;
    height: 22%;
    border-radius: 2px;
    background: #d5e0ed;

    &:first-child {
      width: auto;
      height: auto;
      background: none;
      color: #536985;
      white-space: nowrap;
    }
  }

  b {
    width: 9%;
    height: 45%;
    margin-left: auto;
    border: 1px solid #c6d8ee;
    border-radius: 2px;
    background: #fff;
  }
}

.mock-table {
  flex: 1;
  min-height: 0;
  border: 1px solid #e1e9f3;
  background: #fff;
}

.mock-table-head,
.mock-table-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1.1fr 1fr 0.7fr 0.9fr;
  align-items: center;
  gap: 2%;
  height: 17%;
  min-height: 0;
  padding: 0 2%;
  border-bottom: 1px solid #edf1f7;
}

.mock-table-head {
  height: 14%;
  background: #f3f7fd;
  border-bottom-color: #dce6f2;

  i {
    width: 62%;
    height: 12%;
    min-height: 3px;
    border-radius: 2px;
    background: #acbdd2;
  }
}

.mock-table-row {
  i {
    width: 72%;
    height: 11%;
    min-height: 3px;
    border-radius: 2px;
    background: #d6e1ee;

    &:first-child {
      width: 80%;
      background: #a9c7ea;
    }

    &:last-child {
      width: 74%;
      background: #9ebfe8;
    }
  }

  b {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 66%;
    height: 30%;
    border-radius: 3px;
    background: #fce6e3;
    color: #b35e55;
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
  }

  &:nth-child(3) b {
    background: #e5f5ef;
    color: #228977;
  }

  &:nth-child(4) b {
    background: #fff0da;
    color: #a6742a;
  }
}

.mock-form-layout,
.mock-record-layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 2%;
  flex: 1;
  min-height: 0;
}

.mock-form-main {
  display: flex;
  flex-direction: column;
  gap: 3%;
}

.mock-form-section,
.mock-form-side,
.mock-record-summary,
.mock-record-history {
  border: 1px solid #dfe8f3;
  background: #fff;
}

.mock-form-section {
  height: 31%;
  padding: 2% 3%;

  &:nth-child(2) {
    height: 36%;
  }

  &:nth-child(3) {
    height: 25%;
  }

  span {
    width: auto;
    height: auto;
    min-height: 0;
    background: none;
    color: #355a84;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
  }
}

.mock-form-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12% 4%;
  height: 78%;
  margin-top: 4%;

  i {
    width: 100%;
    height: 62%;
    min-height: 6px;
    border: 1px solid #d6e2ef;
    border-radius: 2px;
    background: #f9fbff;
  }
}

.mock-form-side {
  display: flex;
  flex-direction: column;
  gap: 8%;
  padding: 9%;

  span {
    width: auto;
    height: auto;
    min-height: 0;
    background: none;
    color: #355a84;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
  }

  i {
    width: 100%;
    height: 4%;
    border-bottom: 1px solid #e0e8f1;
    background: #f3f7fb;
  }

  b {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 8%;
    min-height: 22px;
    margin-top: auto;
    border-radius: 3px;
    background: #2173d9;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }
}

.mock-record-layout {
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
}

.mock-record-summary,
.mock-record-history {
  padding: 5%;

  span {
    width: auto;
    height: auto;
    min-height: 0;
    margin-bottom: 7%;
    background: none;
    color: #355a84;
    font-size: 14px;
    font-weight: 800;
    white-space: nowrap;
  }
}

.mock-record-summary {
  i {
    width: 80%;
    height: 2%;
    margin-bottom: 6%;
    border-radius: 2px;
    background: #d0deed;

    &:nth-child(3),
    &:nth-child(7) {
      width: 57%;
    }
  }

  div {
    width: 100%;
    height: 20%;
    margin: 7% 0;
    background: #f2f7fd;
    border: 1px solid #e2ebf5;
  }
}

.mock-record-event {
  position: relative;
  height: 16%;
  margin-left: 3%;
  padding-left: 7%;
  border-left: 1px solid #bad2f1;

  &:before {
    content: '';
    position: absolute;
    top: 2%;
    left: -5px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2876d7;
  }

  i {
    width: auto;
    height: auto;
    min-height: 0;
    margin-bottom: 5%;
    background: none;
    color: #375c89;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }

  b {
    width: 76%;
    height: 10%;
    border-radius: 2px;
    background: #d7e2ee;
  }

  em {
    display: none;
  }
}
</style>
